import arpfLogo from "../assets/arpf-logo.png";
import fmolhsBanner from "../assets/fmolhs-homepage-banner.jpg";
import fmolhsLogo from "../assets/fmolhs-logo-sans-serif.png";

export interface PublicityBannerConfig {
  ariaLabel?: string;
  background?: string;
  ctaLabel?: string;
  imageUrl?: string;
  linkUrl?: string;
  logoUrl?: string;
  subtitle?: string;
  theme?: "dark" | "light";
  title?: string;
}

const readEnvFlag = (
  key: string,
  fallback: boolean,
  env: Record<string, string | undefined>,
) => {
  const rawValue = env[key];
  if (rawValue === undefined) {
    return fallback;
  }

  const normalized = rawValue.trim().toLowerCase();
  if (["0", "false", "no", "off"].includes(normalized)) {
    return false;
  }

  if (["1", "true", "yes", "on"].includes(normalized)) {
    return true;
  }

  return fallback;
};

const FMOLHS_LINK_URL =
  "https://www.fmolhs.org/locations/greater-baton-rouge/our-lady-of-the-lake-regional-medical-center";
const FMOLHS_BACKGROUND =
  "linear-gradient(132.16deg, #00285f 38.16%, #147caa 113.05%)";

export const ARPA_PUBLICITY_BANNER: PublicityBannerConfig = {
  ariaLabel: "Alabama Recreation & Parks Association",
  background: "linear-gradient(90deg, #f7fbf8 0%, #ffffff 54%, #eef7f1 100%)",
  ctaLabel: "Visit arpaonline.org",
  linkUrl: "https://www.arpaonline.org/",
  logoUrl: arpfLogo,
  subtitle: "Supporting safer parks, recreation, and youth sports communities.",
  theme: "light",
  title: "Alabama Recreation & Parks Association",
};

export const FMOLHS_PUBLICITY_BANNER: PublicityBannerConfig = {
  ariaLabel: "FMOL Health",
  background: FMOLHS_BACKGROUND,
  ctaLabel: "Visit fmolhs.org",
  imageUrl: fmolhsBanner,
  linkUrl: FMOLHS_LINK_URL,
  logoUrl: fmolhsLogo,
  subtitle: "Our Lady of the Lake Regional Medical Center",
  theme: "dark",
  title: "FMOL Health",
};

export const DEMO_PUBLICITY_BANNER: PublicityBannerConfig =
  FMOLHS_PUBLICITY_BANNER;

export const isPublicityEnabled = (
  options?: {
    area?: "BACKGROUND" | "COURSE";
    kind?: "BANNER" | "VIDEO";
  },
) => {
  const env = process.env as Record<string, string | undefined>;
  const { area, kind } = options || {};

  const globallyEnabled = readEnvFlag("REACT_APP_PUBLICITY_ENABLED", true, env);
  if (!globallyEnabled) {
    return false;
  }

  if (
    kind &&
    !readEnvFlag(`REACT_APP_${kind}_PUBLICITY_ENABLED`, true, env)
  ) {
    return false;
  }

  if (
    area &&
    !readEnvFlag(`REACT_APP_${area}_PUBLICITY_ENABLED`, true, env)
  ) {
    return false;
  }

  if (
    area &&
    kind &&
    !readEnvFlag(
      `REACT_APP_${area}_${kind}_PUBLICITY_ENABLED`,
      true,
      env,
    )
  ) {
    return false;
  }

  return true;
};

export const normalizePublicityBanner = (
  banner?: any,
): PublicityBannerConfig | null => {
  if (!banner || banner.enabled === false) {
    return null;
  }

  if (typeof banner === "string") {
    return banner.trim() ? { imageUrl: banner } : null;
  }

  const imageUrl =
    banner.imageUrl ||
    banner.image ||
    banner.src ||
    banner.backgroundImageUrl ||
    banner.backgroundImage;
  const logoUrl = banner.logoUrl || banner.logo || banner.logoImageUrl;
  const linkUrl = banner.linkUrl || banner.href || banner.url;
  const title = banner.title || banner.headline;
  const subtitle = banner.subtitle || banner.body || banner.description;
  const background =
    banner.background ||
    banner.backgroundCss ||
    banner.backgroundGradient ||
    banner.gradient;
  const theme = banner.theme;
  const ariaLabel = banner.ariaLabel || banner.alt || banner.altText || title;
  const ctaLabel =
    banner.ctaLabel || banner.buttonLabel || (linkUrl ? "Learn more" : "");

  if (!imageUrl && !logoUrl && !title && !subtitle) {
    return null;
  }

  return {
    ariaLabel,
    background,
    ctaLabel,
    imageUrl,
    linkUrl,
    logoUrl,
    subtitle,
    theme,
    title,
  };
};

const isPublicityBannerExplicitlyDisabled = (banner?: any) =>
  banner === false ||
  (Boolean(banner) &&
    typeof banner === "object" &&
    !Array.isArray(banner) &&
    banner.enabled === false);

export const resolvePublicityBanner = (
  ...candidates: any[]
): PublicityBannerConfig | null => {
  for (const candidate of candidates) {
    if (isPublicityBannerExplicitlyDisabled(candidate)) {
      return null;
    }

    const banner = normalizePublicityBanner(candidate);

    if (banner) {
      return banner;
    }
  }

  return null;
};

export const getEnvPublicityBanner = (
  area: "BACKGROUND" | "COURSE",
): PublicityBannerConfig | null => {
  if (!isPublicityEnabled({ area, kind: "BANNER" })) {
    return null;
  }

  const env = process.env as Record<string, string | undefined>;
  const prefix = `REACT_APP_${area}_BANNER`;

  return normalizePublicityBanner({
    background: env[`${prefix}_BACKGROUND`],
    ctaLabel: env[`${prefix}_CTA_LABEL`],
    imageUrl: env[`${prefix}_IMAGE_URL`],
    linkUrl: env[`${prefix}_LINK_URL`],
    logoUrl: env[`${prefix}_LOGO_URL`],
    subtitle: env[`${prefix}_SUBTITLE`],
    theme: env[`${prefix}_THEME`],
    title: env[`${prefix}_TITLE`],
  });
};
